import { QueuedEvent, TrackingData } from './types';
import { debugLog, generateUUID } from './utils';

export interface TransportConfig {
  endpoint: string;
  apiToken: string;
  maxRetries: number;
  retryBackoff: number;
  debug: boolean;
}

/**
 * Transport layer for sending events to the API
 * Handles retries, queuing, and network failures
 */
export class Transport {
  private config: TransportConfig;
  private queue = new Map<string, QueuedEvent>();
  private retryTimeouts = new Map<string, any>();

  constructor(config: TransportConfig) {
    this.config = config;
  }

  async send(data: TrackingData): Promise<void> {
    const event: QueuedEvent = {
      id: generateUUID(),
      timestamp: Date.now(),
      attempts: 0,
      data,
    };
    await this.sendEvent(event);
  }

  private async sendEvent(event: QueuedEvent): Promise<void> {
    event.attempts++;

    try {
      const payload = {
        type: event.data.type,
        data: this.prepareEventData(event.data),
        token: this.config.apiToken,
      };

      // Try sendBeacon first (except for identify)
      if (navigator.sendBeacon && event.data.type !== 'identify') {
        const blob = new Blob([JSON.stringify(payload)], {
          type: 'application/json',
        });

        if (navigator.sendBeacon(this.config.endpoint, blob)) {
          this.config.debug && debugLog('Event sent', event.data);
          this.removeFromQueue(event.id);
          return;
        }
      }

      // Fallback to fetch
      const response = await fetch(this.config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': this.config.apiToken,
        },
        body: JSON.stringify(payload),
        keepalive: true,
      });

      if (response.ok) {
        this.config.debug && debugLog('Event sent', event.data);
        this.removeFromQueue(event.id);
        return;
      }

      // Rate limit
      if (response.status === 429) {
        const retryAfter = parseInt(
          response.headers.get('Retry-After') || '60',
          10,
        );
        this.config.debug &&
          debugLog(`Rate limited, retry after ${retryAfter}s`);
        this.scheduleRetry(event, retryAfter * 1000);
        return;
      }

      throw new Error(`HTTP ${response.status}`);
    } catch (error) {
      this.config.debug && debugLog('Send failed', { error, event });

      if (event.attempts < this.config.maxRetries) {
        const delay = Math.min(
          this.config.retryBackoff * Math.pow(2, event.attempts - 1),
          30000,
        );
        this.scheduleRetry(event, delay);
      } else {
        this.config.debug &&
          debugLog(`Dropped after ${event.attempts} attempts`);
        this.removeFromQueue(event.id);
      }
    }
  }

  private prepareEventData(data: TrackingData): Record<string, any> {
    const { type, timestamp, ...rest } = data;
    const cleanData: Record<string, any> = {};

    for (const [key, value] of Object.entries(rest)) {
      if (value !== undefined) {
        cleanData[key] = value;
      }
    }

    return cleanData;
  }

  private scheduleRetry(event: QueuedEvent, delay: number): void {
    this.queue.set(event.id, event);

    const existingTimeout = this.retryTimeouts.get(event.id);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const timeout = setTimeout(() => {
      this.retryTimeouts.delete(event.id);
      const queuedEvent = this.queue.get(event.id);
      if (queuedEvent) {
        this.sendEvent(queuedEvent);
      }
    }, delay);

    this.retryTimeouts.set(event.id, timeout);
  }

  private removeFromQueue(eventId: string): void {
    this.queue.delete(eventId);
    const timeout = this.retryTimeouts.get(eventId);
    if (timeout) {
      clearTimeout(timeout);
      this.retryTimeouts.delete(eventId);
    }
  }

  flush(): void {
    const events = Array.from(this.queue.values());
    this.queue.clear();

    for (const timeout of this.retryTimeouts.values()) {
      clearTimeout(timeout);
    }
    this.retryTimeouts.clear();

    for (const event of events) {
      this.sendEvent(event);
    }
  }

  get queueSize(): number {
    return this.queue.size;
  }
}
